import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

type Props = {
  queryParamName: string;
  initialValue: string;
  delay?: number;
};
type Return = {
  /** What the user has typed so far. Updates synchronously on `setValue`. */
  value: string;
  setValue: (newValue: string) => void;
  /** The settled value, i.e. what is in the URL. Trails `value` by `delay`. */
  debouncedValue: string;
};

/**
 * A text value mirrored into a URL query param, debounced.
 *
 * `value` is owned locally and updates synchronously, so a controlled input
 * bound to it never loses a keystroke. Only the debounced value is written to
 * the URL. Writing on every keystroke instead (and reading the input's value
 * back from the URL) drops letters: the Next router applies a URL change
 * asynchronously, and React resets a controlled input to its stale prop value
 * after each change event, so any keystroke that lands before the previous
 * one's URL update settles is wiped. On pages busy enough to freeze the main
 * thread for a moment, several keystrokes land in that window at once.
 *
 * Several components may read the same param on one page (e.g. a table and
 * the summary above it); the ones that never call `setValue` simply see the
 * URL's value as `debouncedValue`.
 */
export const useDebouncedQueryParam = (props: Props): Return => {
  const [paramValue, setParamValue] = useQueryParam(
    props.queryParamName,
    withDefault(StringParam, props.initialValue),
    {
      updateType: "replaceIn",
    },
  );
  const [value, setLocalValue] = useState(paramValue);

  // The URL is applied asynchronously, so a write we made is not in
  // `paramValue` yet when the next keystroke arrives. Two pieces of bookkeeping
  // keep URL changes from clobbering what the user has typed:
  //  - `writeTimer`: set while a keystroke is waiting for its debounced write.
  //    While it is set, no URL change is adopted into `value`.
  //  - `inFlightWrites`: values handed to the router that have not come back
  //    as `paramValue` yet. When one lands it is recognised as our own and not
  //    adopted (the input is already at least that new), even if a later
  //    write has since fired. Without this a slow router (round-trip longer
  //    than `delay`) would make an earlier write revert the input.
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightWrites = useRef<string[]>([]);
  // Latest values, readable from the timer callback without re-creating it.
  const latest = useRef({ paramValue, setParamValue, delay: props.delay });
  latest.current = { paramValue, setParamValue, delay: props.delay };

  const setValue = useCallback((next: string) => {
    setLocalValue(next);
    if (writeTimer.current !== null) {
      clearTimeout(writeTimer.current);
    }
    writeTimer.current = setTimeout(() => {
      writeTimer.current = null;
      const inFlight = inFlightWrites.current;
      if (
        (inFlight.length === 0 && next === latest.current.paramValue) ||
        inFlight[inFlight.length - 1] === next
      ) {
        // The URL already is, or is already becoming, `next`. Writing it
        // again would be dropped by use-query-params as a no-op and never
        // come back, leaving a stale entry in `inFlightWrites`.
        return;
      }
      inFlight.push(next);
      latest.current.setParamValue(next);
    }, latest.current.delay);
  }, []);

  // A pending write must not fire after unmount: on a route change the
  // adapter would replace back to this page's path.
  useEffect(
    () => () => {
      if (writeTimer.current !== null) {
        clearTimeout(writeTimer.current);
      }
    },
    [],
  );

  useEffect(() => {
    const landed = inFlightWrites.current.indexOf(paramValue);
    if (landed !== -1) {
      // One of our own writes came back. Anything written before it has been
      // superseded and will never be adopted. With the pages-router adapter
      // the URL is live, so a write is seen on the very next render and this
      // list rarely holds more than one entry; the bookkeeping is defensive
      // for adapters that expose the URL only after re-rendering, where
      // several writes can be in flight and the router may coalesce them into
      // one landing. Hence the first match, not the last, is cleared up to: a
      // later duplicate may still be on its way. Entries left behind by such
      // coalescing only ever suppress the adoption of an external change to
      // that exact value, and are cleared by the next landing or external
      // change.
      inFlightWrites.current.splice(0, landed + 1);
      return;
    }
    // A URL change that did not come from typing (back/forward, a sibling
    // clearing the param, the router becoming ready). Adopt it unless the
    // user is mid-word.
    if (writeTimer.current === null) {
      inFlightWrites.current = [];
      setLocalValue(paramValue);
    }
  }, [paramValue]);

  return useMemo(
    () => ({
      value,
      setValue,
      debouncedValue: paramValue,
    }),
    [value, setValue, paramValue],
  );
};
